import { decode, encode } from '@/base64';
import { MultipleChoiceEdit } from './MultipleChoiceEdit';
import { RandomGroupEdit } from './RandomGroupEdit';
import { OpenQuestionEdit } from './OpenQuestionEdit';
import { SortGroupsEdit } from './SortGroupsEdit';
import { ShowOnPictureEdit } from './ShowOnPictureEdit';
import { MatrixEdit } from './MatrixEdit';
import { OrderVraagEdit } from './OrderEdit';
import { ConnectEdit } from './ConnectEdit';
import { BoxErrorEdit } from './BoxErrorEdit';
import { NamePartsQuestion } from './NamePartsQuestion';
import { UploadEdit } from './UploadEdit';
import { MathEdit } from './MathEdit';
import { ImageShowPartsEdit } from './ImageShowPartsEdit';
import { TabbedContentBoxEdit } from './TabbedContentBoxEdit';
import { InvulVraagEdit } from './InvulVraagEdit';
import { InstructionEdit } from './InstructionEdit';
import { BoxQuestionEditOld } from './BoxQuestionEditOld';
import { NotificationContext } from '@/components/Contexts/NotificationContext';
import { Subject, BehaviorSubject, skip } from 'rxjs';
import React, { useContext } from 'react';
import ReactDOM from 'react-dom';
import { BoxQuestionEdit } from './BoxQuestionEdit';
import { Button } from '@teo/components';
import { Trash } from '@teo/components/icons';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { setShowNotification } from '@/App';
import { copyToClipboard, hasParentWithClass, orderWidgets } from '@/util';
import { ChatGpt } from './ChatGPT/ChatGpt';
import { WidgetOptions } from './widgetOptions/WidgetOptions';
import { countsTowardQuestionNumber } from './questionNumbering';

export function getEditor(state) {
  let Edit;
  switch (state.type) {
    case 'InvulVraag':
      Edit = InvulVraagEdit;
      break;
    case 'MeerKeuze':
      Edit = MultipleChoiceEdit;
      break;
    case 'OpenVraag':
      Edit = OpenQuestionEdit;
      break;
    case 'SortGroups':
      Edit = SortGroupsEdit;
      break;
    case 'ShowOnPicture':
      Edit = ShowOnPictureEdit;
      break;

    case 'NameImage':
    case 'NameImage2':
      Edit = NamePartsQuestion;
      break;
    case 'MatrixVraag2':
      Edit = MatrixEdit;
      break;
    case 'OrderVraag':
      Edit = OrderVraagEdit;
      break;
    case 'Link':
      Edit = ConnectEdit;
      break;
    case 'KofferVraag2':
      Edit = BoxQuestionEdit;
      break;
    case 'KofferVraag':
      Edit = BoxQuestionEditOld;
      break;
    case 'Math':
      Edit = MathEdit;
      break;
    case 'FileUpload':
      Edit = UploadEdit;
      break;
    case 'ImageShowParts':
      Edit = ImageShowPartsEdit;
      break;
    case 'TabbedContentBoxes':
      Edit = TabbedContentBoxEdit;
      break;
    case 'BoxError':
      Edit = BoxErrorEdit;
      break;
    case 'Instruction':
      Edit = InstructionEdit;
      break;
    case 'InvulVraagGroup':
    case 'MeerKeuzeGroup':
    case 'OpenVraagGroup':
    case 'NameImage2Group':
    case 'MatrixVraag2Group':
    case 'OrderVraagGroup':
    case 'SortGroupsGroup':
    case 'ShowOnPictureGroup':
    case 'LinkGroup':
    case 'FileUploadGroup':
      Edit = RandomGroupEdit;
      break;
  }
  return Edit;
}

const WIDGET_FIELD_SELECTOR = [
  'input',
  'textarea',
  'select',
  '[contenteditable="true"]',
  '[data-froala-widget-field]',
].join(',');

const TEO_ALLOW_WIDGET_DROP = '__teoAllowWidgetDrop';

const GUARDED_WIDGET_EVENTS = [
  'keydown',
  'keyup',
  'beforeinput',
  'compositionstart',
  'compositionupdate',
  'compositionend',
  'paste',
  'cut',
  'drop',
];

function installWidgetPlaceholderEventGuard(placeholder) {
  if (!placeholder || placeholder.__teoWidgetEventGuardInstalled) {
    return;
  }

  placeholder.__teoWidgetEventGuardInstalled = true;

  const shouldGuard = (event) => {
    /*
      Some widgets, like NameImage2, use react-dnd with a body-level dropzone.

      In those cases, the widget can mark the native drop event during capture:
        ev.nativeEvent.__teoAllowWidgetDrop = true;

      Then this placeholder guard will not stop propagation, so react-dnd can
      still complete the drop.
    */
    if (event.type === 'drop' && event[TEO_ALLOW_WIDGET_DROP]) {
      return false;
    }

    const target = event.target;

    if (!(target instanceof Element)) {
      return false;
    }

    if (!placeholder.contains(target)) {
      return false;
    }

    /*
      Only guard actual interactive fields inside the widget.

      Important: closest('[contenteditable="true"]') may otherwise walk up to
      Froala's outer editable root outside this widget placeholder. So we only
      accept the match if the matched field is also inside this placeholder.
    */
    const field = target.closest(WIDGET_FIELD_SELECTOR);

    return Boolean(field && placeholder.contains(field));
  };

  const guard = (event) => {
    if (!shouldGuard(event)) {
      return;
    }

    /*
      Do NOT preventDefault().

      Backspace/Delete should still work normally inside the input/textarea.
      We only stop the event from bubbling from the widget into Froala.
    */
    event.stopPropagation();
  };

  for (const eventName of GUARDED_WIDGET_EVENTS) {
    /*
      Bubble phase on purpose:
      - the actual input/widget gets the event first
      - Froala does not receive it afterwards
    */
    placeholder.addEventListener(eventName, guard, false);
  }
}

export function renderEditor(state, target, undo, lessonId, i = 0) {
  let Editor = getEditor(state);
  const uid = String(state?.uid || '');

  if (!uid) {
    console.error('Unable to render editor: widget uid missing', state);
    return null;
  }

  const escapedUid = CSS.escape(uid);
  const placeholder =
    target?.id === uid
      ? target
      : target?.querySelector?.(`#${escapedUid}`) || null;

  if (!placeholder) {
    console.error('Unable to render editor: placeholder not found', state);
    return null;
  }

  const editorRoot = placeholder.closest('.fr-view') || placeholder;
  const froalaBox = placeholder.closest('.fr-box');

  let hasParentWidget;
  installWidgetPlaceholderEventGuard(placeholder);

  // Only add if missing; and use canonical formatting to avoid flip-flop diffs
  const currentStyle = placeholder.getAttribute('style') || '';
  if (!/\bposition\s*:\s*relative\b/i.test(currentStyle)) {
    // set canonical style that sanitizer/browser also emits
    placeholder.setAttribute(
      'style',
      (currentStyle ? currentStyle.trim().replace(/;?$/, '; ') : '') +
        'position: relative;'
    );
  }
  placeholder.innerHTML = '';

  const parentNode = placeholder.parentNode;
  hasParentWidget = parentNode?.closest('.widget-placeholder') !== null;

  if (!Editor) {
    console.error('Error: rendering', state);
    return null;
  }

  if (
    hasParentWidget &&
    (state.type != 'Math' ||
      state?.type != 'ImageShowParts' ||
      state?.type != 'TabbedContentBoxes')
  ) {
    const prevElement = placeholder.previousElementSibling;
    if (prevElement && prevElement.tagName.toLowerCase() === 'p') {
      prevElement.remove();
    }

    // placeholder.remove();
    orderWidgets(editorRoot);
    undo?.reset();
  }

  let widget = {
    onModified: new Subject(),
    lessonId: lessonId,
    state: state,
  };

  const handleFocus = () => {
    if (
      state?.type != 'Math' &&
      state?.type != 'Instruction' &&
      state?.type != 'MeerKeuzeGroup' &&
      state?.type != 'OrderVraagGroup' &&
      state?.type != 'OpenVraagGroup' &&
      state?.type != 'SortGroupsGroup' &&
      state?.type != 'ShowOnPictureGroup' &&
      state?.type != 'MatrixVraag2Group' &&
      state?.type != 'NameImage2Group' &&
      state?.type != 'InvulVraagGroup' &&
      state?.type != 'FileUploadGroup' &&
      state?.type != 'LinkGroup'
    ) {
      const activeWidget = placeholder.ownerDocument.createElement('div');
      activeWidget.className +=
        'remove_me_gEE8QIPZ6LEay22SdSCU rounded-lg border border-secondary-04 absolute inset-0 -z-[1] active-widget';
      placeholder.appendChild(activeWidget);
    }
  };
  const handleBlur = () => {
    const divsToRemove = placeholder.getElementsByClassName('active-widget');
    Array.from(divsToRemove).forEach((div) => {
      div.remove();
    });

    const enableToolbarGroup = (widgetType) => {
      const toolbarWidget = froalaBox?.querySelector(
        `.fr-command[data-param1="${widgetType}"]`
      );
      const toolbarGroup = toolbarWidget?.parentNode;

      toolbarGroup?.querySelectorAll('a').forEach((btn) => {
        btn.classList.remove('fr-disabled');
      });
    };

    enableToolbarGroup('MeerKeuze');
    enableToolbarGroup('TabbedContentBoxes');
  };

  setTimeout(() => {
    orderWidgets(editorRoot);
  }, 0);

  widget.portal = ReactDOM.createPortal(
    state.inline ? (
      <span>
        <Editor
          setStateFn={(fn) => (widget.stateFn = fn)}
          onModified={() => widget.onModified.next()}
          lessonId={lessonId}
          state={state}
          index={i}
        />
      </span>
    ) : (
      <div
        className={`${
          !state?.noSubmit &&
          state?.type != 'Math' &&
          state?.type != 'ImageShowParts' &&
          state?.type != 'TabbedContentBoxes'
            ? 'widget_practical '
            : ''
        }widget-editor-row flex flex-row items-center`}
        onFocus={
          (state?.type != 'Math' ||
            state?.type != 'ImageShowParts' ||
            state?.type != 'TabbedContentBoxes') &&
          handleFocus
        }
        onBlur={
          (state?.type != 'Math' ||
            state?.type != 'ImageShowParts' ||
            state?.type != 'TabbedContentBoxes') &&
          handleBlur
        }
      >
        <Editor
          setStateFn={(fn) => (widget.stateFn = fn)}
          onModified={() => widget.onModified.next()}
          lessonId={lessonId}
          state={state}
          index={i}
        />
        {state?.type !== 'Instruction' && (
          <div className="flex shrink-0 flex-col gap-3">
            <WidgetOptions
              widget={widget}
              placeholder={placeholder}
              state={state}
              undo={undo}
              chatGPT={
                state?.type == 'MeerKeuze' ||
                state?.type == 'OpenVraag' ||
                state?.type == 'Link' ||
                state?.type == 'OrderVraag'
              }
            />
          </div>
        )}
      </div>
    ),
    placeholder
  );

  return widget;
}

export function renderChatGpt(chatUid, node, chatContent, reload) {
  node.innerHTML = '';

  let chatGpt = {
    state: chatUid,
  };

  chatGpt.portal = ReactDOM.createPortal(
    <div>
      <ChatGpt
        reload={reload}
        chatUid={chatUid.uid}
        chatContent={chatContent}
      />
    </div>,
    node
  );

  return chatGpt;
}

export function renderEditors(
  parent,
  undo,
  lessonId,
  correction,
  waitForResult
) {
  if (!parent) console.error('Parent missing', parent);

  const widgets = parent.querySelectorAll('.widget-placeholder');

  let index = 1;

  return [...widgets].reduce((acc, widget, i) => {
    if (widget.state) return acc; //renderWidget(widget.state, widget, i + 1);
    let node = widget.querySelectorAll('div[data-state],span[data-state]');

    if (node.length === 0) return acc;
    let stateStr = node[0].getAttribute('data-state');

    try {
      widget.state = JSON.parse(stateStr); //legacy parsing
    } catch (e) {
      widget.state = JSON.parse(decode(stateStr)); //base64 parsing
    }
    acc[widget.state.uid] = renderEditor(
      widget.state,
      widget,
      undo,
      lessonId,
      index
    );
    if (countsTowardQuestionNumber(widget.state)) index += 1;

    //if (!acc[widget.state.uid]) console.warn("Something went wrong rendering", widget.state)
    return acc;
  }, {});
}

export function rerenderEditors(parent, undo, lessonId, oldState, oldChats) {
  if (!parent) console.error('Parent missing', parent);
  const widgets = parent.getElementsByClassName('widget-placeholder');
  const chats = parent.getElementsByClassName('chat-gpt-placeholder');
  let index = 1;
  let stateChat = [];
  let stateWidget = [];
  if (oldState.length > 0) {
    const widgetsArr = [...widgets].filter(
      (widget, i) => !hasParentWithClass(widget, 'widget-placeholder')
    );
    widgetsArr.map((widget, i) => {
      // if (widget.state) return acc; //renderWidget(widget.state, widget, i + 1);
      widget.state = oldState.find((el) => el.uid === widget.id);
      const el = renderEditor(widget.state, widget, undo, lessonId, index);
      if (countsTowardQuestionNumber(widget.state)) index += 1;
      stateWidget.push(el);
    }, {});
  }
  [...chats].map((chat, i) => {
    chat.state = oldChats.find((el) => el.uid === chat.id);
    if (chat.state && !chat.classList.contains('close-chat')) {
      const chatResultat = chat.querySelectorAll('.chat_resultat');
      const prevResultat = chatResultat[0]?.childNodes[0];
      const el = renderChatGpt(chat.state, chat, prevResultat, true);
      stateChat.push(el);
    }
  }, {});
  return { stateWidget, stateChat };
}
